import { useState, useMemo, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  FileText, Video, Image as ImageIcon, BookOpen, Filter, Search, Loader2,
  AlertCircle, ExternalLink, Link2, Share2, Check, Users, HeartPulse,
  Stethoscope, GraduationCap, Briefcase, Newspaper, Megaphone,
  HandHeart, RotateCcw,
} from 'lucide-react'
import { PortalLayout } from '../../components/portal/PortalLayout'
import { Seo } from '../../components/portal/Seo'
import {
  Container, Section, SectionHeading, Breadcrumbs, Card, Badge, EmptyState, Alert, FormField,
} from '../../components/portal/ui'
import {
  listResources, getResourceFacets, shareResource,
  RESOURCE_TYPE_LABELS, AUDIENCE_LABELS, LANGUAGE_LABELS,
  CATEGORY_KEYS, CATEGORY_LABELS, TOPIC_KEYS, TOPIC_LABELS,
  type Resource, type ResourceCategory, type ResourceAudience, type ResourceFacets,
} from '../../services/resourceService'
import { TranslatedContentNotice } from '../../components/portal/TranslatedContentNotice'

// ================================================================================
// RESOURCE LIBRARY — new information architecture.
//
// Instead of a flat PDF-upload list, the library is organised by 8 audience-oriented
// CATEGORIES (deep-linkable via ?category=) with live counts from /resources/facets,
// plus the four filters the brief calls out: Language | Format | Audience | Topic.
// Cards link to REAL official Central TB Division / Ni-kshay material (externalUrl),
// surface an "Official source" badge, and offer social-share buttons that hit the
// public POST /resources/{id}/share counter — so social-media campaigns can promote
// this information and we can measure reach.
// ================================================================================

// Icon per category — used on the 8 tiles and on cards.
const CATEGORY_ICON: Record<ResourceCategory, typeof Users> = {
  PATIENT_RESOURCES: HeartPulse,
  FAMILY_RESOURCES: Users,
  HEALTHCARE_PROFESSIONALS: Stethoscope,
  NGOS: HandHeart,
  SCHOOLS_COLLEGES: GraduationCap,
  CORPORATES: Briefcase,
  MEDIA: Newspaper,
  CAMPAIGN_MATERIALS: Megaphone,
}

const TYPE_ICON: Record<string, typeof FileText> = {
  GUIDELINE: FileText, REPORT: FileText, TRAINING: Video, INFOGRAPHIC: ImageIcon,
  VIDEO: Video, PUBLICATION: BookOpen, NOTIFICATION: FileText, MEDIA: ImageIcon,
  IEC_MATERIAL: FileText, CAMPAIGN_TOOLKIT: Megaphone, REPORTING_FORMAT: FileText, OTHER: FileText,
}

// Format (= resource type) quick-pick keys for the Format filter dropdown.
const FORMAT_KEYS: string[] = ['all', ...Object.keys(RESOURCE_TYPE_LABELS)]
const AUDIENCE_KEYS: string[] = ['all', ...(Object.keys(AUDIENCE_LABELS) as ResourceAudience[])]
const TOPIC_SELECT_KEYS: string[] = ['all', ...TOPIC_KEYS]
const LANG_KEYS: string[] = ['all', 'en', 'hi', 'mr', 'multi']

const size = 12

export default function ResourcesPage() {
  const { t, i18n } = useTranslation()
  const [params, setParams] = useSearchParams()

  // Filters — all four from the brief, plus category (the IA) and free-text search.
  const initialCategory = params.get('category') || 'all'
  const [category, setCategory] = useState(initialCategory)
  const [format, setFormat] = useState('all')
  const [audience, setAudience] = useState('all')
  const [topic, setTopic] = useState('all')
  const [lang, setLang] = useState('all')
  const [q, setQ] = useState('')

  const [items, setItems] = useState<Resource[]>([])
  const [facets, setFacets] = useState<ResourceFacets | null>(null)
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Translated label resolvers (fall back to the stable label constants).
  const typeLabel = (key: string) =>
    t(`pages.resources.types.${key}`, { defaultValue: RESOURCE_TYPE_LABELS[key as keyof typeof RESOURCE_TYPE_LABELS] ?? key })
  const audienceLabel = (key: string) =>
    t(`pages.resources.audiences.${key}`, { defaultValue: AUDIENCE_LABELS[key as ResourceAudience] ?? key })
  const langLabel = (key: string) =>
    t(`pages.resources.langs.${key}`, { defaultValue: LANGUAGE_LABELS[key] ?? key })
  const categoryLabel = (key: string) =>
    t(`pages.resources.categories.${key}`, { defaultValue: CATEGORY_LABELS[key as ResourceCategory] ?? key })
  const topicLabel = (key: string) =>
    t(`pages.resources.topics.${key}`, { defaultValue: TOPIC_LABELS[key as keyof typeof TOPIC_LABELS] ?? key })

  // Debounce free-text search so typing doesn't fire a request per keystroke.
  const [qDebounced, setQDebounced] = useState('')
  useEffect(() => {
    const td = setTimeout(() => setQDebounced(q), 300)
    return () => clearTimeout(td)
  }, [q])

  // Reflect the category filter in the URL (shareable deep links — the IA entry points).
  useEffect(() => {
    const next = new URLSearchParams(params)
    if (category === 'all') next.delete('category')
    else next.set('category', category)
    setParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [data, fac] = await Promise.all([
        listResources({
          type: format === 'all' ? undefined : format,
          audience: audience === 'all' ? undefined : audience,
          category: category === 'all' ? undefined : category,
          topic: topic === 'all' ? undefined : topic,
          language: lang === 'all' ? undefined : lang,
          q: qDebounced || undefined,
          page,
          size,
        }),
        getResourceFacets(),
      ])
      setItems(data.content)
      setTotal(data.totalElements)
      setTotalPages(data.totalPages)
      setFacets(fac)
    } catch (e: any) {
      setError(e?.response?.data?.message || t('pages.stories.error', { defaultValue: 'Could not load resources. Please try again.' }))
    } finally {
      setLoading(false)
    }
  }, [format, audience, category, topic, lang, qDebounced, page, t])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(0) }, [format, audience, category, topic, lang, qDebounced])

  const facetCount = (bucket: 'categories' | 'formats' | 'audiences' | 'topics', key: string): number => {
    const list = facets?.[bucket]
    if (!list) return 0
    return list.find((b) => b.key === key)?.count ?? 0
  }

  const activeFilters = useMemo(
    () =>
      [
        category !== 'all' && `${t('pages.resources.filters.categoryLabel')}: ${categoryLabel(category)}`,
        format !== 'all' && `${t('pages.resources.filters.formatLabel')}: ${typeLabel(format)}`,
        audience !== 'all' && `${t('pages.resources.filters.audienceLabel')}: ${audienceLabel(audience)}`,
        topic !== 'all' && `${t('pages.resources.filters.topicLabel')}: ${topicLabel(topic)}`,
        lang !== 'all' && `${t('pages.resources.filters.langLabel')}: ${langLabel(lang)}`,
        qDebounced && `${t('common.search')}: “${qDebounced}”`,
      ].filter((x): x is string => Boolean(x)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [category, format, audience, topic, lang, qDebounced, t]
  )

  const clearFilters = () => {
    setCategory('all'); setFormat('all'); setAudience('all'); setTopic('all'); setLang('all'); setQ('')
  }

  const metadataItems = t('pages.resources.metadata.items', { returnObjects: true }) as string[]

  return (
    <PortalLayout>
      <Seo
        title={t('pages.resources.seo.title')}
        description={t('pages.resources.seo.description')}
        canonicalPath="/resources"
        breadcrumbs={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.resources', 'Resources'), path: '/resources' }]}
      />
      <Section className="bg-gradient-mesh pb-6">
        <Container>
          <Breadcrumbs items={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.resources', 'Resources') }]} />
          <SectionHeading as="h1" eyebrow={t('pages.resources.hero.eyebrow')} title={t('pages.resources.hero.title')} description={t('pages.resources.hero.description')} />
        </Container>
      </Section>

      {/* 8 category tiles — the library information architecture, deep-linkable. */}
      <Section className="bg-white pt-2">
        <Container>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CATEGORY_KEYS.map((key) => {
              const Icon = CATEGORY_ICON[key]
              const count = facetCount('categories', key)
              const active = category === key
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setCategory(active ? 'all' : key)}
                  aria-pressed={active}
                  className={`text-left rounded-xl border p-4 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(15_76_129)] ${
                    active
                      ? 'border-[rgb(15_76_129)] bg-[rgb(240_252_255)] ring-1 ring-[rgb(15_76_129)]'
                      : 'border-snow-200 bg-white hover:border-[rgb(15_76_129)]/40 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-lg bg-[rgb(240_252_255)] text-[rgb(15_76_129)] flex items-center justify-center shrink-0" aria-hidden="true">
                      <Icon className="w-5 h-5" />
                    </span>
                    <div className="min-w-0">
                      <h2 className="font-semibold text-snow-900 text-sm leading-tight">{categoryLabel(key)}</h2>
                      <p className="text-xs text-snow-500 mt-0.5">{t('pages.resources.categoryCount', { count, defaultValue: `${count} resources` })}</p>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </Container>
      </Section>

      <Section className="bg-white pt-2">
        <Container>
          {/* Filters — Language | Format | Audience | Topic, plus search */}
          <Card className="p-5 mb-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <FormField label={t('pages.resources.filters.langLabel', { defaultValue: 'Language' })} htmlFor="res-lang">
                <select id="res-lang" value={lang} onChange={(e) => setLang(e.target.value)} className="select">
                  {LANG_KEYS.map((k) => <option key={k} value={k}>{langLabel(k)}</option>)}
                </select>
              </FormField>
              <FormField label={t('pages.resources.filters.formatLabel', { defaultValue: 'Format' })} htmlFor="res-format">
                <select id="res-format" value={format} onChange={(e) => setFormat(e.target.value)} className="select">
                  {FORMAT_KEYS.map((k) => <option key={k} value={k}>{typeLabel(k)}</option>)}
                </select>
              </FormField>
              <FormField label={t('pages.resources.filters.audienceLabel', { defaultValue: 'Audience' })} htmlFor="res-audience">
                <select id="res-audience" value={audience} onChange={(e) => setAudience(e.target.value)} className="select">
                  {AUDIENCE_KEYS.map((k) => <option key={k} value={k}>{audienceLabel(k)}</option>)}
                </select>
              </FormField>
              <FormField label={t('pages.resources.filters.topicLabel', { defaultValue: 'Topic' })} htmlFor="res-topic">
                <select id="res-topic" value={topic} onChange={(e) => setTopic(e.target.value)} className="select">
                  {TOPIC_SELECT_KEYS.map((k) => (
                    <option key={k} value={k}>{topicLabel(k)}{k !== 'all' ? ` (${facetCount('topics', k)})` : ''}</option>
                  ))}
                </select>
              </FormField>
            </div>
            <div className="grid sm:grid-cols-1 lg:grid-cols-4 gap-4 mt-4">
              <FormField label={t('pages.resources.filters.searchLabel')} htmlFor="res-search">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-snow-400" aria-hidden="true" />
                  <input id="res-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('pages.resources.filters.searchPlaceholder')} className="input pl-9" />
                </div>
              </FormField>
            </div>
            {activeFilters.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap mt-4 pt-4 border-t border-snow-100">
                <Filter className="w-4 h-4 text-snow-400" aria-hidden="true" />
                <span className="text-xs text-snow-500">{t('pages.resources.filters.activeFilters')}</span>
                {activeFilters.map((f) => <Badge key={f} tone="slate">{f}</Badge>)}
                <button onClick={clearFilters} className="inline-flex items-center gap-1 text-xs text-[rgb(15_76_129)] hover:underline ml-1">
                  <RotateCcw className="w-3 h-3" aria-hidden="true" /> {t('pages.resources.filters.clearAll')}
                </button>
              </div>
            )}
          </Card>

          {category !== 'all' && (
            <div className="flex items-center gap-2 mb-4">
              <Badge tone="teal">{categoryLabel(category)}</Badge>
              <button onClick={() => setCategory('all')} className="text-xs text-[rgb(15_76_129)] hover:underline">
                {t('pages.resources.filters.clearCategory', { defaultValue: 'Show all categories' })}
              </button>
            </div>
          )}

          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-snow-500" aria-live="polite">
              {loading ? t('pages.resources.loading') : t('common.resourcesCount', { count: total })}
            </p>
          </div>

          {error ? (
            <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg text-sm mb-4" role="alert">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-16 text-snow-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> {t('pages.resources.loadingResources')}</div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Filter className="w-7 h-7" />}
              title={t('pages.resources.empty.title')}
              description={activeFilters.length > 0
                ? t('pages.resources.empty.descFiltered')
                : t('pages.resources.empty.descEmpty')}
              action={<button onClick={clearFilters} className="btn-primary btn-sm">{t('pages.resources.filters.clearAll')}</button>}
            />
          ) : (
            <>
              {items.some((r) => r.matchedLanguage && r.matchedLanguage !== (i18n.language?.split('-')[0] || 'en')) && (
                <TranslatedContentNotice matchedLanguage="en" />
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {items.map((r) => (
                  <ResourceCard
                    key={r.id}
                    resource={r}
                    typeLabel={typeLabel}
                    audienceLabel={audienceLabel}
                    langLabel={langLabel}
                    categoryLabel={categoryLabel}
                    topicLabel={topicLabel}
                    shareLabel={t('pages.resources.share.label', { defaultValue: 'Share' })}
                    copyLabel={t('pages.resources.share.copy', { defaultValue: 'Copy link' })}
                    copiedLabel={t('pages.resources.share.copied', { defaultValue: 'Copied' })}
                    officialLabel={t('pages.resources.officialSource', { defaultValue: 'Official source' })}
                    openLabel={t('pages.resources.openResource', { defaultValue: 'Open official resource' })}
                    sourcePrefix={t('pages.resources.sourcePrefix', { defaultValue: 'Source: ' })}
                    featuredLabel={t('pages.resources.featured', { defaultValue: 'Featured' })}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="btn-outline btn-sm disabled:opacity-50">{t('common.previous')}</button>
                  <span className="text-xs text-snow-500">{t('common.pageXofY', { page: page + 1, total: totalPages })}</span>
                  <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="btn-outline btn-sm disabled:opacity-50">{t('common.next')}</button>
                </div>
              )}
            </>
          )}
        </Container>
      </Section>

      {/* Metadata schema note */}
      <Section className="bg-snow-50 pt-0">
        <Container>
          <Card className="p-6">
            <h2 className="font-semibold text-snow-900">{t('pages.resources.metadata.title')}</h2>
            <ul className="grid sm:grid-cols-2 gap-2 mt-3 text-sm text-snow-600">
              {metadataItems.map((m, i) => (
                <li key={i} className="flex items-center gap-2"><FileText className="w-4 h-4 text-snow-400" aria-hidden="true" /> {m}</li>
              ))}
            </ul>
          </Card>
        </Container>
      </Section>
    </PortalLayout>
  )
}

// ---------------------------------------------------------------------
// Resource card — links to the official external resource, shows an
// "Official source" badge, and offers social-share buttons (which also
// increment the public share counter for campaign analytics).
// ---------------------------------------------------------------------

interface ResourceCardProps {
  resource: Resource
  typeLabel: (k: string) => string
  audienceLabel: (k: string) => string
  langLabel: (k: string) => string
  categoryLabel: (k: string) => string
  topicLabel: (k: string) => string
  shareLabel: string
  copyLabel: string
  copiedLabel: string
  officialLabel: string
  openLabel: string
  sourcePrefix: string
  featuredLabel: string
}

function ResourceCard(props: ResourceCardProps) {
  const { resource: r } = props
  const [copied, setCopied] = useState(false)
  const Icon = TYPE_ICON[r.resourceType] ?? FileText
  const href = r.externalUrl || r.fileUrl || ''
  const isOfficial = !!r.externalUrl

  const shareUrl = href || (typeof window !== 'undefined' ? `${window.location.origin}/resources?category=${r.category ?? 'all'}` : '')
  const shareText = `${r.title} — TB Mukt Bharat resource library`

  const trackShare = async (channel: string) => {
    // Fire the public share counter (best-effort — never block the share window).
    try { await shareResource(r.id) } catch { /* analytics only */ }
    // eslint-disable-next-line no-console
    console.debug('resource share', channel, r.id)
  }

  const openShare = (channel: 'whatsapp' | 'twitter' | 'facebook' | 'linkedin') => {
    const u = encodeURIComponent(shareUrl)
    const txt = encodeURIComponent(shareText)
    const map: Record<string, string> = {
      whatsapp: `https://wa.me/?text=${txt}%20${u}`,
      twitter: `https://twitter.com/intent/tweet?text=${txt}&url=${u}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
    }
    trackShare(channel)
    window.open(map[channel], '_blank', 'noopener,noreferrer,width=600,height=540')
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      trackShare('copy')
      setTimeout(() => setCopied(false), 1800)
    } catch { /* clipboard may be unavailable */ }
  }

  return (
    <Card className="p-5 h-full flex flex-col">
      <div className="flex items-start gap-3 mb-3">
        <span className="w-10 h-10 rounded-lg bg-[rgb(240_252_255)] text-[rgb(15_76_129)] flex items-center justify-center shrink-0" aria-hidden="true">
          <Icon className="w-5 h-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-snow-900 line-clamp-2">{r.title}</h3>
          <p className="text-xs text-snow-500 mt-0.5">{props.typeLabel(r.resourceType)}</p>
        </div>
        {r.isFeatured && <Badge tone="teal">{props.featuredLabel}</Badge>}
      </div>

      {r.description && <p className="text-sm text-snow-600 line-clamp-3 flex-1">{r.description}</p>}

      <div className="flex flex-wrap items-center gap-1.5 mt-3">
        {r.category && <Badge tone="blue">{props.categoryLabel(r.category)}</Badge>}
        {r.topic && <Badge tone="slate">{props.topicLabel(r.topic)}</Badge>}
        {r.audience && <Badge tone="slate">{props.audienceLabel(r.audience)}</Badge>}
        {r.language && <Badge tone="slate">{props.langLabel(r.language)}</Badge>}
        {r.fileType && <Badge tone="slate">{r.fileType}</Badge>}
      </div>

      {isOfficial && (
        <div className="inline-flex items-center gap-1 mt-3 text-[11px] font-medium text-emerald-700 bg-emerald-50 rounded-md px-2 py-1 w-fit">
          <Check className="w-3.5 h-3.5" aria-hidden="true" /> {props.officialLabel}
        </div>
      )}
      {r.sourceOrg && <p className="text-[11px] text-snow-500 mt-2">{props.sourcePrefix}{r.sourceOrg}</p>}

      {/* Open the official resource + social-share row */}
      <div className="mt-4 pt-3 border-t border-snow-100">
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[rgb(15_76_129)] hover:underline min-h-[44px]"
          >
            <ExternalLink className="w-4 h-4" aria-hidden="true" />
            {isOfficial ? props.openLabel : 'Download'}
          </a>
        ) : (
          <span className="text-xs text-snow-400">—</span>
        )}

        <div className="flex items-center gap-1 mt-2">
          <span className="inline-flex items-center gap-1 text-[11px] text-snow-500 mr-1">
            <Share2 className="w-3.5 h-3.5" aria-hidden="true" /> {props.shareLabel}
          </span>
          <ShareBtn label="WhatsApp" title="Share on WhatsApp" onClick={() => openShare('whatsapp')}>
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.037zm5.323-10.393c-.082-.138-.301-.222-.629-.378-.328-.156-1.939-.957-2.239-1.067-.3-.11-.519-.165-.737.165-.219.329-.848 1.066-1.04 1.284-.191.219-.383.246-.711.082-.328-.164-1.385-.51-2.638-1.627-.975-.868-1.634-1.94-1.826-2.269-.191-.329-.02-.507.144-.671.148-.148.329-.383.493-.574.164-.191.219-.329.328-.548.11-.219.055-.411-.027-.574-.082-.164-.737-1.776-1.01-2.43-.267-.64-.538-.553-.738-.562l-.629-.011c-.219 0-.574.082-.875.411-.301.329-1.149 1.122-1.149 2.733 0 1.611 1.176 3.167 1.34 3.386.164.219 2.309 3.526 5.592 4.944.782.337 1.392.538 1.869.688.785.25 1.5.214 2.064.13.63-.094 1.939-.792 2.213-1.558.274-.766.274-1.422.192-1.558z"/></svg>
          </ShareBtn>
          <ShareBtn label="X / Twitter" title="Share on X" onClick={() => openShare('twitter')}>
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          </ShareBtn>
          <ShareBtn label="Facebook" title="Share on Facebook" onClick={() => openShare('facebook')}>
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          </ShareBtn>
          <ShareBtn label="LinkedIn" title="Share on LinkedIn" onClick={() => openShare('linkedin')}>
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
          </ShareBtn>
          <ShareBtn label={copied ? props.copiedLabel : props.copyLabel} title={props.copyLabel} onClick={copyLink}>
            {copied ? <Check className="w-4 h-4" aria-hidden="true" /> : <Link2 className="w-4 h-4" aria-hidden="true" />}
          </ShareBtn>
        </div>
      </div>
    </Card>
  )
}

function ShareBtn({ label, title, onClick, children }: { label: string; title: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-snow-200 text-snow-600 hover:bg-snow-50 hover:text-[rgb(15_76_129)] transition min-h-[44px] min-w-[44px]"
    >
      {children}
      <span className="sr-only">{label}</span>
    </button>
  )
}